import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Button,
} from '@mui/material';
import { ArrowBack, AccessTime } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/authService';

interface OutOfSiteTaskData {
  id: number;
  technician_id: number;
  created_by: number;
  objective: string;
  status: string;
  start_time: string | null;
  expected_end_time: string | null;
  end_time: string | null;
  notes: string | null;
  attachments: string[] | null;
  technician?: { id: number; user?: { id: number; name: string; name_ar?: string } };
  creator?: { id: number; name: string; name_ar?: string };
}

const statusColors: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'error',
};

const formatDT = (dt: string | null, lang: string) => {
  if (!dt) return '-';
  return new Date(dt).toLocaleString(lang === 'ar' ? 'ar' : 'en', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kuwait',
  });
};

const formatDuration = (start: string | null, end: string | null) => {
  if (!start) return '-';
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const mins = Math.round((e - s) / 60000);
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const OutOfSiteTaskDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [data, setData] = useState<OutOfSiteTaskData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/out-of-site-tasks/${id}`);
        setData(res.data);
      } catch (e) {
        console.error(e);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <Box textAlign="center" py={10}><CircularProgress /></Box>;
  if (!data) return <Box textAlign="center" py={10}><Typography color="error">Record not found</Typography></Box>;

  const techName = data.technician?.user ? (language === 'ar' && data.technician.user.name_ar ? data.technician.user.name_ar : data.technician.user.name) : '-';
  const creatorName = data.creator ? (language === 'ar' && data.creator.name_ar ? data.creator.name_ar : data.creator.name) : '-';

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="small">
          {t('common.back') || 'Back'}
        </Button>
        <Typography variant="h4">{t('outOfSiteTask.detail.title') || 'Out-of-Site Task Detail'}</Typography>
        <Chip
          label={data.status.replace('_', ' ')}
          color={statusColors[data.status] || 'default'}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        {/* Main Info */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('outOfSiteTask.detail.info') || 'Information'}</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('outOfSiteTask.detail.objective') || 'Objective'}</Typography>
                  <Typography fontWeight="bold">{data.objective}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('outOfSiteTask.detail.technician') || 'Technician'}</Typography>
                  <Typography fontWeight="bold">{techName}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('outOfSiteTask.detail.createdBy') || 'Created By'}</Typography>
                  <Typography fontWeight="bold">{creatorName}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('outOfSiteTask.detail.status') || 'Status'}</Typography>
                  <Chip label={data.status.replace('_', ' ')} color={statusColors[data.status] || 'default'} size="small" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Timing */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom><AccessTime sx={{ verticalAlign: 'middle', mr: 1 }} />{t('outOfSiteTask.detail.timing') || 'Timing'}</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('outOfSiteTask.detail.startTime') || 'Start Time'}</Typography>
                  <Typography fontWeight="bold">{formatDT(data.start_time, language)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('outOfSiteTask.detail.expectedEnd') || 'Expected End'}</Typography>
                  <Typography fontWeight="bold">{formatDT(data.expected_end_time, language)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('outOfSiteTask.detail.endTime') || 'End Time'}</Typography>
                  <Typography fontWeight="bold">{formatDT(data.end_time, language)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('outOfSiteTask.detail.duration') || 'Duration'}</Typography>
                  <Typography fontWeight="bold">{formatDuration(data.start_time, data.end_time)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Notes */}
        {data.notes && (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('outOfSiteTask.detail.notes') || 'Notes'}</Typography>
                <Typography>{data.notes}</Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OutOfSiteTaskDetail;
